/* eslint-disable camelcase */
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Company from "../misc/Company";
import UserFileLog from "../misc/UserFileLog";
import MemberTag from "../../../component/ui/tabs/MemberTag";

import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getUserDetails } from "../../../services/apis/user";
import Button from "../../../component/element/forms/Button";
import { getInitials } from "../../../utility/getIntinials";
import { get_company } from "../../../services/apis/company";

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="flex items-start gap-4 py-3 border-b border-gray-100/20 last:border-0">
    <span className="w-36 shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-400 pt-0.5">
      {label}
    </span>
    <span className="text-sm text-gray-800 font-medium">{value}</span>
  </div>
);

const UserDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { email } = useParams();
  const navigate = useNavigate();

  const user = useAppSelector(({ user }) => user.user);
  // const company = useAppSelector(({ company }) => company.company);
  const loading = useAppSelector(({ user }) => user.loading);

  //required id from the user details to fetch company details, backend currently does not return this. Will need to update this in the future to avoid multiple api calls
  useEffect(() => {
    const fetchCompany = async () => {
      if (!user) return;
      try {
        await get_company(user.profile.company_id, dispatch);
      } catch (error) {
        console.error("Error fetching company details:", error);
      }
    };

    fetchCompany();
  }, [dispatch, user]);

  const onLoad = React.useCallback(() => {
    if (email) getUserDetails(email, dispatch);
  }, [dispatch, email]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <div>User not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">User Details</h2>
        </div>
        <Button
          title="Back"
          children={<HiOutlineArrowLongLeft size={18} className="mr-1.5" />}
          onClick={() => navigate(-1)}
          className="flex items-center px-4 py-2 rounded-lg border border-gray-200/20 text-white text-sm font-medium hover:bg-gray-50 shadow-sm transition-colors"
        />
      </div>

      {/* Profile Card */}

      <div className="bg-white rounded-2xl border border-gray-200/20 shadow-sm mb-4 overflow-hidden">
        <div className="p-6 flex flex-col md:flex-row gap-6">
          {/* Avatar */}
          <div className="w-full md:w-56 md:shrink-0 aspect-square md:aspect-auto md:h-auto rounded-xl border-2 border-gray-100 shadow-sm bg-gray-50 overflow-hidden relative">
            {user?.profile?.avatar ? (
              <img
                src={user.profile.avatar}
                alt={user.profile.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <span className="absolute inset-0 flex items-center justify-center text-2xl bg-gray-100/30 text-gray-500 font-medium">
                {getInitials(user?.profile.name || "—")}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold text-gray-900 truncate mb-0">
                {user?.profile?.name || "—"}
              </h3>
            </div>

            <div className="divide-y divide-gray-100">
              <InfoRow label="Email" value={user?.email || "—"} />
              <InfoRow
                label="Mobile Number"
                value={user?.profile?.phone || "—"}
              />
              <InfoRow
                label="Membership"
                value={
                  <MemberTag
                    value={user?.role === "0" ? "Member" : "Company Admin"}
                    className={
                      user?.role === "1"
                        ? "bg-amber-500 text-white text-xs px-2.5 py-0.5 rounded-full font-medium"
                        : "bg-blue-100 text-blue-700 text-xs px-2.5 py-0.5 rounded-full font-medium"
                    }
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700">Company</h4>
          </div>
          <div className="p-5">
            <Company company_id={user?.profile?.company_id || ""} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700">File Logs</h4>
          </div>
          <div className="p-5">
            <UserFileLog email={email || ""} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
